import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { CardFlag, McqExtra, ReviewRating } from '@yorukiri/shared';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';
import { BigCard } from '../components/BigCard';
import { McqPlayer } from '../components/McqPlayer';
import { ProgressSegments, type SegmentState } from '../components/ProgressSegments';
import { RateButtons } from '../components/RateButtons';
import { ReviewTopbar } from '../components/ReviewTopbar';
import { Undo } from '../components/icons';
import {
  type ApiCard,
  type ApiDueCard,
  type ExtraMode,
  useCardAction,
  useCardFlag,
  useCards,
  useDueCards,
  useExtraCards,
  usePage,
  useRateCard,
  useUndoReview,
} from '../lib/queries';

// The session holds its queue as a snapshot taken when the page first loads.
// Rated cards drop out; Again cards push to the end. Queue length grows to fit
// those repeats so ProgressSegments stays honest.
type QueueItem = { card: ApiCard; pageTitle: string };

export function ReviewRoute() {
  const { pageId: rawPageId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('review');
  const isAll = rawPageId === 'all';
  const extraMode = searchParams.get('mode') as ExtraMode | null;
  const extraDays = Number(searchParams.get('days') ?? 7);
  const extraLimit = Number(searchParams.get('limit') ?? 50);
  const useExtra = extraMode !== null;

  // Three possible data sources: cross-deck due queue, single-deck due queue,
  // or an extra-review custom queue (cram / ahead / new / failed).
  const dueAllQuery = useDueCards();
  const pageCardsQuery = useCards(isAll ? undefined : rawPageId, {
    descendants: true,
    due: true,
  });
  const extraQuery = useExtraCards({
    pageId: isAll ? undefined : rawPageId,
    mode: extraMode ?? 'cram',
    days: extraDays,
    limit: extraLimit,
    descendants: true,
    enabled: useExtra,
  });
  const pageMetaQuery = usePage(isAll ? undefined : rawPageId);

  const activeQueryData = useExtra
    ? extraQuery.data
    : isAll
    ? dueAllQuery.data
    : pageCardsQuery.data;
  const cardsLoaded = activeQueryData !== undefined;
  const sourceCards: (ApiCard & { pageTitle?: string })[] = activeQueryData ?? [];

  const extraLabel = useExtra
    ? extraMode === 'ahead'
      ? t('mode_ahead', { days: extraDays })
      : extraMode === 'cram'
      ? t('mode_cram')
      : extraMode === 'new'
      ? t('mode_new')
      : t('mode_failed')
    : '';
  const baseTitle = isAll ? t('all_decks') : pageMetaQuery.data?.page.title ?? '';
  const headerTitle = useExtra ? `${baseTitle} · ${extraLabel}` : baseTitle;

  // The queue is built once when cards first load; Again cards push onto the
  // end during the session. total grows with Again pushes so the progress bar
  // stays honest.
  const [queue, setQueue] = useState<QueueItem[] | null>(null);
  const [position, setPosition] = useState(1);
  const [total, setTotal] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [history, setHistory] = useState<SegmentState[]>([]);
  const [elapsedSec, setElapsedSec] = useState(0);
  // reviewIds[i] is the server-issued review id for history[i]. Undo uses the
  // most recent one to reverse the server state, not just local history.
  const [reviewIds, setReviewIds] = useState<string[]>([]);
  const initializedRef = useRef(false);
  const startRef = useRef<number | null>(null);
  const cardShownAtRef = useRef<number>(Date.now());
  const rateCard = useRateCard();
  const undoReview = useUndoReview();
  const cardAction = useCardAction();
  const cardFlag = useCardFlag();

  // Reset when switching between sessions (e.g., /review/d1 → /review/d2).
  useEffect(() => {
    initializedRef.current = false;
    startRef.current = null;
    cardShownAtRef.current = Date.now();
    setQueue(null);
    setPosition(1);
    setTotal(0);
    setRevealed(false);
    setHistory([]);
    setReviewIds([]);
    setElapsedSec(0);
  }, [rawPageId]);

  // Reset the per-card timer whenever the front-of-queue card changes.
  useEffect(() => {
    cardShownAtRef.current = Date.now();
  }, [queue?.[0]?.card.id]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!cardsLoaded) return;
    initializedRef.current = true;
    const items: QueueItem[] = sourceCards.map((c) => {
      const apiDue = c as ApiDueCard;
      return {
        card: c,
        pageTitle: apiDue.pageTitle ?? headerTitle,
      };
    });
    setQueue(items);
    setTotal(items.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsLoaded]);

  useEffect(() => {
    if (!queue || queue.length === 0) return;
    if (startRef.current === null) startRef.current = Date.now();
    const id = setInterval(() => {
      if (startRef.current) {
        setElapsedSec(Math.floor((Date.now() - startRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [queue]);

  const current = queue && queue.length > 0 ? queue[0] : null;

  const handleReveal = useCallback(() => setRevealed(true), []);

  const advance = useCallback(
    (rating: ReviewRating) => {
      if (!current) return;
      const cardId = current.card.id;
      const elapsedMs = Date.now() - cardShownAtRef.current;
      // Fire the rating. The server returns a reviewId we use for undo. If it
      // errors, we still advance locally — losing a single rating beats freezing.
      rateCard.mutate(
        { cardId, rating, elapsedMs },
        {
          onSuccess: (res) => setReviewIds((ids) => [...ids, res.reviewId]),
          onError: () => setReviewIds((ids) => [...ids, '']),
        },
      );

      setHistory((h) => [
        ...h,
        rating === 'again' ? 'again' : rating === 'hard' ? 'hard' : 'ok',
      ]);
      setPosition((p) => p + 1);
      setRevealed(false);
      setQueue((q) => {
        if (!q || q.length === 0) return q;
        const [head, ...rest] = q;
        if (rating === 'again') {
          // Push back onto the queue so the learner sees it again this session.
          setTotal((n) => n + 1);
          return [...rest, head!];
        }
        return rest;
      });
    },
    [current, rateCard],
  );

  const handleRate = useCallback(
    (rating: ReviewRating) => {
      if (!revealed) return;
      advance(rating);
    },
    [revealed, advance],
  );

  // MCQ reveals via Submit with a correctness signal. Map that signal to a
  // sensible rating so the scheduler still gets input: correct → good, wrong → again.
  const handleMcqReveal = useCallback((wasCorrect: boolean) => {
    setRevealed(true);
    // Auto-rate once revealed so MCQ doesn't require a separate button press.
    // The user can still override by pressing again/hard/good/easy before moving on.
    pendingMcqRatingRef.current = wasCorrect ? 'good' : 'again';
  }, []);
  const pendingMcqRatingRef = useRef<ReviewRating | null>(null);

  // After MCQ reveal, auto-advance on next space/enter unless the user picks a rating.
  useEffect(() => {
    if (!revealed) {
      pendingMcqRatingRef.current = null;
    }
  }, [revealed]);

  const handleUndo = useCallback(() => {
    // True undo: reverse the last server-side review and rewind local history.
    // If we never got a reviewId back (offline / network hiccup), we can't
    // reverse the server side — just rewind the local view.
    if (history.length === 0) return;
    const lastReviewId = reviewIds[reviewIds.length - 1];
    if (lastReviewId) {
      undoReview.mutate({ reviewId: lastReviewId });
    }
    setHistory((h) => h.slice(0, -1));
    setReviewIds((ids) => ids.slice(0, -1));
    setPosition((p) => Math.max(1, p - 1));
    setRevealed(true);
  }, [history.length, reviewIds, undoReview]);

  const handleFlag = useCallback(
    (flag: CardFlag | null) => {
      if (!current) return;
      cardFlag.mutate({ cardId: current.card.id, flag });
    },
    [current, cardFlag],
  );

  const handleSuspend = useCallback(() => {
    if (!current) return;
    const cardId = current.card.id;
    cardAction.mutate({ cardId, action: 'suspend' });
    // Drop this card from the queue — we don't want to show a suspended card.
    setQueue((q) => (q ? q.slice(1) : q));
    setHistory((h) => [...h, 'ok']);
    setPosition((p) => p + 1);
    setRevealed(false);
  }, [current, cardAction]);

  const handleBury = useCallback(() => {
    if (!current) return;
    const cardId = current.card.id;
    cardAction.mutate({ cardId, action: 'bury' });
    setQueue((q) => (q ? q.slice(1) : q));
    setHistory((h) => [...h, 'ok']);
    setPosition((p) => p + 1);
    setRevealed(false);
  }, [current, cardAction]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA'].includes(e.target.tagName))
        return;
      if (!current) return;
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        if (!revealed && current.card.type !== 'mcq') handleReveal();
        else if (revealed && pendingMcqRatingRef.current) {
          advance(pendingMcqRatingRef.current);
        }
      } else if (revealed && ['1', '2', '3', '4'].includes(e.key)) {
        const map: Record<string, ReviewRating> = {
          '1': 'again',
          '2': 'hard',
          '3': 'good',
          '4': 'easy',
        };
        handleRate(map[e.key]!);
      } else if (e.key === 'u' || e.key === 'U') {
        handleUndo();
      } else if (e.key === '-' || (e.shiftKey && e.key === '@')) {
        handleSuspend();
      } else if (e.key === 'b' || e.key === 'B') {
        handleBury();
      } else if (['!', '@', '#', '$'].includes(e.key)) {
        const flagMap: Record<string, CardFlag> = {
          '!': 'red',
          '@': 'orange',
          '#': 'green',
          $: 'blue',
        };
        handleFlag(flagMap[e.key] ?? null);
      } else if (e.key === '0') {
        handleFlag(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    revealed,
    handleReveal,
    handleRate,
    handleUndo,
    handleSuspend,
    handleBury,
    handleFlag,
    advance,
    current,
  ]);

  const segments = useMemo<SegmentState[]>(() => {
    const arr: SegmentState[] = [];
    for (let i = 0; i < total; i++) {
      if (i < history.length) arr.push(history[i]!);
      else if (i === history.length) arr.push('current');
      else arr.push('pending');
    }
    return arr;
  }, [history, total]);

  const stats = useMemo(() => {
    const got = history.filter((s) => s === 'ok').length;
    const again = history.filter((s) => s === 'again').length;
    const mm = Math.floor(elapsedSec / 60);
    const ss = String(elapsedSec % 60).padStart(2, '0');
    return { got, again, elapsed: `${mm}:${ss}` };
  }, [history, elapsedSec]);

  if (!cardsLoaded) {
    return <Centered>…</Centered>;
  }
  if (!queue || queue.length === 0) {
    if (history.length === 0) return <EmptySession />;
    return <CompleteScreen stats={stats} />;
  }

  const currentCard = current!.card;
  const currentPageTitle = current!.pageTitle;

  return (
    <div
      style={{
        background: colors.surface,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage:
          'radial-gradient(ellipse at 50% 100%, rgba(59, 42, 255, 0.04), transparent 60%)',
      }}
    >
      <ReviewTopbar
        pageTitle={headerTitle}
        position={Math.min(position, total)}
        total={total}
        stats={stats}
      />
      <ProgressSegments
        segments={segments}
        position={Math.min(position, total)}
        total={total}
        togo={Math.max(0, total - position + 1)}
      />

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 40px 30px',
          gap: 26,
          position: 'relative',
        }}
      >
        {currentCard.type === 'mcq' ? (
          <McqPlayer
            question={currentCard.front}
            extra={currentCard.extra as McqExtra}
            revealed={revealed}
            onReveal={handleMcqReveal}
          />
        ) : (
          <BigCard
            card={currentCard}
            revealed={revealed}
            onReveal={handleReveal}
            pageTitle={isAll ? currentPageTitle : undefined}
          />
        )}

        {revealed && <RateButtons onRate={handleRate} />}
        {!revealed && currentCard.type !== 'mcq' && (
          <div
            style={{
              display: 'flex',
              gap: 24,
              fontVariantNumeric: 'tabular-nums',
              fontSize: 10,
              letterSpacing: '0.06em',
              color: colors.ink3,
              marginTop: 4,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Kbd>{t('kbd_reveal')}</Kbd> {t('reveal')}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Kbd>{t('kbd_undo')}</Kbd> {t('undo')}
            </span>
          </div>
        )}

        <button
          onClick={handleUndo}
          disabled={history.length === 0}
          style={{
            position: 'absolute',
            top: 14,
            right: 24,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 10,
            color: colors.ink3,
            background: colors.surface2,
            border: `1px solid ${colors.line2}`,
            padding: '5px 10px',
            borderRadius: 5,
            cursor: history.length === 0 ? 'default' : 'pointer',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'inline-flex',
            gap: 6,
            alignItems: 'center',
            fontFamily: 'inherit',
            opacity: history.length === 0 ? 0.4 : 1,
          }}
        >
          <Undo size={11} /> {t('undo')}
        </button>

        <ActionBar
          currentFlag={currentCard.flag ?? null}
          currentState={currentCard.state ?? 'new'}
          onFlag={handleFlag}
          onSuspend={handleSuspend}
          onBury={handleBury}
        />
      </div>
    </div>
  );
}

function ActionBar({
  currentFlag,
  currentState,
  onFlag,
  onSuspend,
  onBury,
}: {
  currentFlag: CardFlag | null;
  currentState: string;
  onFlag: (f: CardFlag | null) => void;
  onSuspend: () => void;
  onBury: () => void;
}) {
  const { t } = useTranslation('review');
  const flags: { key: CardFlag; color: string; kbd: string }[] = [
    { key: 'red', color: '#E04A4A', kbd: '!' },
    { key: 'orange', color: '#E08A2C', kbd: '@' },
    { key: 'green', color: '#3BA85A', kbd: '#' },
    { key: 'blue', color: '#4A78E0', kbd: '$' },
  ];
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: 24,
        right: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 10,
        color: colors.ink3,
        letterSpacing: '0.06em',
      }}
    >
      <span style={stateBadge(currentState)}>{currentState.toUpperCase()}</span>
      <div style={{ display: 'flex', gap: 4 }}>
        {flags.map((f) => {
          const active = currentFlag === f.key;
          return (
            <button
              key={f.key}
              onClick={() => onFlag(active ? null : f.key)}
              title={`${f.key} · ${f.kbd}`}
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `1.5px solid ${f.color}`,
                background: active ? f.color : 'transparent',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          );
        })}
      </div>
      <div style={{ flex: 1 }} />
      <button onClick={onBury} style={secondaryBtn} title="Bury · B">
        {t('bury')} <Kbd>B</Kbd>
      </button>
      <button onClick={onSuspend} style={secondaryBtn} title="Suspend · -">
        {t('suspend')} <Kbd>-</Kbd>
      </button>
    </div>
  );
}

function stateBadge(state: string): React.CSSProperties {
  const map: Record<string, string> = {
    new: colors.ink2,
    learning: colors.warn,
    review: colors.accent,
    relearning: colors.warn,
    suspended: colors.bad,
    buried: colors.ink3,
  };
  const tone = map[state] ?? colors.ink3;
  return {
    fontSize: 9,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: tone,
    background: `${tone}1a`,
    padding: '3px 7px',
    borderRadius: 3,
    fontWeight: 500,
  };
}

const secondaryBtn: React.CSSProperties = {
  fontSize: 10,
  color: colors.ink3,
  background: 'transparent',
  border: `1px solid ${colors.line2}`,
  padding: '4px 8px',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
};

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      style={{
        background: 'rgba(24,22,15,0.06)',
        padding: '1px 6px',
        borderRadius: 3,
        fontSize: 9.5,
        fontFamily: 'inherit',
      }}
    >
      {children}
    </kbd>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.ink3,
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}

function EmptySession() {
  const { t } = useTranslation('review');
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        background: colors.surface,
      }}
    >
      <div style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em' }}>
        {t('home_empty')}
      </div>
      <Link to="/" style={backLink}>
        ← {t('back_home')}
      </Link>
    </div>
  );
}

function CompleteScreen({ stats }: { stats: { got: number; again: number; elapsed: string } }) {
  const { t } = useTranslation('review');
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        background: colors.surface,
      }}
    >
      <div
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.accent,
        }}
      >
        {t('complete_label')}
      </div>
      <div style={{ fontSize: 44, fontWeight: 600, letterSpacing: '-0.02em' }}>
        {t('complete_heading')}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 40,
          fontVariantNumeric: 'tabular-nums',
          color: colors.ink3,
          fontSize: 13,
        }}
      >
        <Stat label={t('stats.got')} value={stats.got} />
        <Stat label={t('stats.again')} value={stats.again} />
        <Stat label={t('stats.elapsed')} value={stats.elapsed} />
      </div>
      <Link to="/" style={backLink}>
        ← {t('back_home')}
      </Link>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: colors.ink3,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 500, color: colors.ink }}>{value}</div>
    </div>
  );
}

const backLink: React.CSSProperties = {
  fontSize: 13,
  color: colors.ink3,
  textDecoration: 'none',
  border: `1px solid ${colors.line2}`,
  padding: '8px 14px',
  borderRadius: 6,
};
